package org.moxultimate.topwithmox.productcrud.main

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.cards.ContentCard
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.theme.*
import org.w3c.dom.events.Event

/**
 * ProductCRUDPage
 * Main CRUD landing page at /product-crud.
 * Shows product content sections with product card grids, edit overlays on hover,
 * and add/edit controls. Mirrors the public ProductPage layout with CRUD actions.
 */
@Composable
fun ProductCRUDPage() {
    val viewModel: ProductCRUDViewModel = koinViewModel()
    val listState by viewModel.listState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val hasMoreInCurrentView by viewModel.hasMoreInCurrentView.collectAsState()
    val allTypes by viewModel.allTypes.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val searchText by viewModel.searchText.collectAsState()
    val crudState by viewModel.crudState.collectAsState()
    val navigator = rememberAppNavigator()

    // Retain last success to avoid flickering during filter reloads.
    var lastSuccess by remember { mutableStateOf<ProductContentListState.Success?>(null) }
    if (listState is ProductContentListState.Success) {
        lastSuccess = listState as ProductContentListState.Success
    }

    val isFilterReloading = listState is ProductContentListState.Loading && lastSuccess != null

    LaunchedEffect(Unit) {
        viewModel.loadContents()
        viewModel.loadAllTypes()
        viewModel.loadAllTags()
    }

    LaunchedEffect(crudState) {
        if (crudState is ProductCRUDState.Success) {
            viewModel.loadContents()
            viewModel.resetState()
        }
    }

    Box(
        modifier = Modifier.fillMaxWidth().fillMaxHeight().backgroundColor(WhiteBG),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = listState) {
            is ProductContentListState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    ProductCRUDListContent(
                        state = cached,
                        allTypes = allTypes,
                        selectedTypes = selectedTypes,
                        allTags = allTags,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMore = hasMoreInCurrentView,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                        onAddClick = { navigator.goToProductAddPage() },
                        onContentClick = { navigator.goToProductUpdatePage(it) },
                        onAddProductClick = { navigator.goToProductItemAddPage(it) },
                        onDeleteClick = { viewModel.deleteContent(it) },
                    )
                } else {
                    LoadingIndicator()
                }
            }

            is ProductContentListState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.message,
                onTryAgain = { viewModel.loadContents() },
            )

            is ProductContentListState.Success -> ProductCRUDListContent(
                state = state,
                allTypes = allTypes,
                selectedTypes = selectedTypes,
                allTags = allTags,
                searchText = searchText,
                isLoadingMore = isLoadingMore,
                hasMore = hasMoreInCurrentView,
                onTypeToggle = { viewModel.toggleType(it) },
                onSearchChange = { viewModel.setSearchText(it) },
                onClearFilters = { viewModel.clearAllFilters() },
                onLoadMore = { viewModel.loadMoreIfNeeded() },
                onAddClick = { navigator.goToProductAddPage() },
                onContentClick = { navigator.goToProductUpdatePage(it) },
                onAddProductClick = { navigator.goToProductItemAddPage(it) },
                onDeleteClick = { viewModel.deleteContent(it) },
            )
        }

        // Overlay is always in the DOM — CSS opacity transition drives smooth fade.
        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

/**
 * ProductCRUDListContent
 * Scrollable layout: header, search bar, type chips, and product-page-style
 * section cards. Attaches an infinite-scroll listener that triggers [onLoadMore].
 *
 * @param state Current success state with loaded contents.
 * @param allTypes Full type list for the chip row.
 * @param selectedTypes Currently active type filter chips.
 * @param allTags Tag list used as autocomplete suggestions.
 * @param searchText Live text filter.
 * @param isLoadingMore True while the next page is being fetched.
 * @param hasMore False when all pages have been loaded.
 * @param onTypeToggle Toggles a type chip on/off.
 * @param onSearchChange Updates [searchText] and re-filters.
 * @param onClearFilters Resets all chips and search text.
 * @param onLoadMore Triggered by the scroll handler to fetch the next page.
 * @param onAddClick Opens the add content page.
 * @param onContentClick Opens the update page for the given content.
 * @param onAddProductClick Opens the add product page for a given contentId.
 */
@OptIn(DelicateApi::class)
@Composable
private fun ProductCRUDListContent(
    state: ProductContentListState.Success,
    allTypes: List<String>,
    selectedTypes: Set<String>,
    allTags: List<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    onTypeToggle: (String) -> Unit,
    onSearchChange: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
    onAddClick: () -> Unit,
    onContentClick: (ProductContentResponse) -> Unit,
    onAddProductClick: (Long) -> Unit,
    onDeleteClick: (Long) -> Unit,
) {
    DisposableEffect(hasMore) {
        val handler: (Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMore && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    val hasActiveFilters = selectedTypes.isNotEmpty() || searchText.isNotBlank()

    Column(
        modifier = if (isMobile)
            Modifier.fillMaxWidth().padding(leftRight = 16.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem),
    ) {
        // Header row
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 80.px, bottom = 32.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                SpanText(
                    text = "Manage Product Content",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(if (isMobile) 22.px else 32.px)
                        .fontWeight(FontWeight.Bold)
                        .moxTextGradient(),
                )
                SpanText(
                    text = "${state.contents.size} contents loaded",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray),
                )
            }
            GradientButton(
                text = "+ Add Content",
                buttonHeight = if (isMobile) 36 else 60,
                onClick = onAddClick,
            )
        }

        // Search bar row with optional Clear Filters button
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.px),
        ) {
            Box(modifier = Modifier.flexGrow(1)) {
                SearchBarWithAutocomplete(
                    value = searchText,
                    onValueChange = onSearchChange,
                    suggestions = allTags,
                    placeholder = "Search product content…",
                    onSearch = onSearchChange,
                )
            }

            if (hasActiveFilters) {
                Row(
                    modifier = PressableButtonStyle.toModifier()
                        .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                        .borderRadius(16.px)
                        .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                        .styleModifier { property("background", "#FFFFFF") }
                        .onClick { onClearFilters() },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "Clear Filters",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                            .moxTextGradient(),
                    )
                }
            }
        }

        // Type filter chips — scrollable row
        if (allTypes.isNotEmpty()) {
            Row(
                modifier = ChipRowScrollStyle.toModifier()
                    .fillMaxWidth()
                    .padding(top = 12.px, bottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                allTypes.forEach { type ->
                    Chip(
                        description = type,
                        isSelected = type in selectedTypes,
                        onChipClick = { onTypeToggle(type) },
                    )
                }
            }
        }

        // Content card grid
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = 30.px)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
            horizontalArrangement = Arrangement.spacedBy(24.px),
        ) {
            state.contents.forEach { content ->
                ContentCard(
                    imageSrc = content.thumbnailPath.orEmpty(),
                    type = content.type,
                    title = content.title,
                    description = content.description,
                    modifier = if (isMobile)
                        Modifier.fillMaxWidth().margin(bottom = 24.px)
                    else
                        Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 24.px),
                    onDeleteClick = {
                        if (window.confirm("Delete \"${content.title}\"? This will also remove its images and cannot be undone.")) {
                            onDeleteClick(content.id)
                        }
                    },
                    actions = {
                        GradientButton(
                            text = "Edit Content",
                            buttonHeight = 42,
                            onClick = { onContentClick(content) },
                        )
                        GradientButton(
                            text = "+ Product",
                            buttonHeight = 42,
                            onClick = { onAddProductClick(content.id) },
                        )
                    },
                )
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) { LoadingMoreIndicator() }
        }
    }
}

